import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

const ChangePasswordScreen = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'Home' },
    { label: 'Modifier le mot de passe', path: '/change-password', icon: 'Lock' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Le mot de passe actuel est requis';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe est requis';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (formData.currentPassword && formData.newPassword && formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe doit être différent de l\'ancien';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Vérifier d'abord le mot de passe actuel en essayant de se reconnecter
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        return;
      }

      // Tenter de se reconnecter avec le mot de passe actuel pour le valider
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: formData.currentPassword
      });

      if (signInError) {
        setErrors({ currentPassword: 'Le mot de passe actuel est incorrect' });
        toast.error('Le mot de passe actuel est incorrect');
        return;
      }

      // Mettre à jour le mot de passe
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (updateError) {
        throw updateError;
      }

      toast.success('Mot de passe modifié avec succès');
      
      // Réinitialiser le formulaire
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setErrors({});

    } catch (error) {
      console.error('Erreur lors de la modification du mot de passe:', error);
      toast.error(error.message || 'Une erreur est survenue lors de la modification du mot de passe');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    if (strength <= 2) return { strength: 1, label: 'Faible', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 2, label: 'Moyen', color: 'bg-yellow-500' };
    return { strength: 3, label: 'Fort', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-foreground flex items-center gap-3 mb-2">
                <Icon name="Lock" size={32} color="var(--color-primary)" />
                Modifier le mot de passe
              </h1>
              <p className="text-muted-foreground">
                Mettez à jour votre mot de passe pour sécuriser votre compte
              </p>
            </div>

            {/* Card */}
            <div className="bg-card border border-border rounded-lg shadow-sm">
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="Shield" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      Sécurité du compte
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Assurez-vous d'utiliser un mot de passe fort et unique
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Current Password */}
                <div className="relative">
                  <Input
                    label="Mot de passe actuel"
                    type={showCurrentPassword ? "text" : "password"}
                    placeholder="Entrez votre mot de passe actuel"
                    value={formData.currentPassword}
                    onChange={(e) => handleChange('currentPassword', e.target.value)}
                    error={errors.currentPassword}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-9 p-1 hover:bg-muted rounded transition-colors"
                  >
                    <Icon 
                      name={showCurrentPassword ? "EyeOff" : "Eye"} 
                      size={18} 
                      className="text-muted-foreground"
                    />
                  </button>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-sm font-medium text-foreground mb-4">
                    Nouveau mot de passe
                  </h3>

                  {/* New Password */}
                  <div className="space-y-4">
                    <div className="relative">
                      <Input
                        label="Nouveau mot de passe"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Entrez un nouveau mot de passe"
                        value={formData.newPassword}
                        onChange={(e) => handleChange('newPassword', e.target.value)}
                        error={errors.newPassword}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-9 p-1 hover:bg-muted rounded transition-colors"
                      >
                        <Icon 
                          name={showNewPassword ? "EyeOff" : "Eye"} 
                          size={18} 
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.newPassword && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Force du mot de passe:</span>
                          <span className={`font-medium ${
                            passwordStrength.strength === 1 ? 'text-red-500' :
                            passwordStrength.strength === 2 ? 'text-yellow-500' :
                            'text-green-500'
                          }`}>
                            {passwordStrength.label}
                          </span>
                        </div>
                        <div className="flex gap-1 h-1">
                          <div className={`flex-1 rounded-full ${passwordStrength.strength >= 1 ? passwordStrength.color : 'bg-muted'}`} />
                          <div className={`flex-1 rounded-full ${passwordStrength.strength >= 2 ? passwordStrength.color : 'bg-muted'}`} />
                          <div className={`flex-1 rounded-full ${passwordStrength.strength >= 3 ? passwordStrength.color : 'bg-muted'}`} />
                        </div>
                      </div>
                    )}

                    {/* Confirm Password */}
                    <div className="relative">
                      <Input
                        label="Confirmer le nouveau mot de passe"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirmez votre nouveau mot de passe"
                        value={formData.confirmPassword}
                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                        error={errors.confirmPassword}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-9 p-1 hover:bg-muted rounded transition-colors"
                      >
                        <Icon 
                          name={showConfirmPassword ? "EyeOff" : "Eye"} 
                          size={18} 
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Info" size={16} className="text-primary" />
                    Exigences du mot de passe
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icon 
                        name={formData.newPassword.length >= 8 ? "CheckCircle2" : "Circle"} 
                        size={16} 
                        className={formData.newPassword.length >= 8 ? "text-green-500 mt-0.5" : "mt-0.5"} 
                      />
                      <span>Au moins 8 caractères</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon 
                        name={/[A-Z]/.test(formData.newPassword) ? "CheckCircle2" : "Circle"} 
                        size={16} 
                        className={/[A-Z]/.test(formData.newPassword) ? "text-green-500 mt-0.5" : "mt-0.5"} 
                      />
                      <span>Au moins une lettre majuscule</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon 
                        name={/[a-z]/.test(formData.newPassword) ? "CheckCircle2" : "Circle"} 
                        size={16} 
                        className={/[a-z]/.test(formData.newPassword) ? "text-green-500 mt-0.5" : "mt-0.5"} 
                      />
                      <span>Au moins une lettre minuscule</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon 
                        name={/\d/.test(formData.newPassword) ? "CheckCircle2" : "Circle"} 
                        size={16} 
                        className={/\d/.test(formData.newPassword) ? "text-green-500 mt-0.5" : "mt-0.5"} 
                      />
                      <span>Au moins un chiffre</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon 
                        name={/[^a-zA-Z\d]/.test(formData.newPassword) ? "CheckCircle2" : "Circle"} 
                        size={16} 
                        className={/[^a-zA-Z\d]/.test(formData.newPassword) ? "text-green-500 mt-0.5" : "mt-0.5"} 
                      />
                      <span>Au moins un caractère spécial (recommandé)</span>
                    </li>
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4">
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    loading={isSubmitting}
                    iconName="Check"
                    iconPosition="left"
                    iconSize={18}
                  >
                    Modifier le mot de passe
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => {
                      setFormData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                      setErrors({});
                    }}
                    disabled={isSubmitting}
                  >
                    Réinitialiser
                  </Button>
                </div>
              </form>
            </div>

            {/* Security Tips */}
            <div className="mt-6 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Shield" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                    Conseils de sécurité
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <li>• Utilisez un mot de passe unique pour ce compte</li>
                    <li>• Évitez d'utiliser des informations personnelles facilement devinables</li>
                    <li>• Changez votre mot de passe régulièrement</li>
                    <li>• Ne partagez jamais votre mot de passe avec qui que ce soit</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChangePasswordScreen;